import { addCalendarDays } from "@common/helpers/date";
import { type Task, TaskStatus } from "@common/types/task";
import type {
    DemoDataset,
    DemoRunKind,
    DemoRunRecord,
    DemoViewerRecord,
} from "./busyClosesDemo.types";
import { buildDemoRun } from "./buildDemoRun";
import {
    DEMO_ACCOUNT_NAMES,
    DEMO_TASK_PRIORITIES,
    DEMO_TASK_STATUSES,
    DEMO_TASK_TEMPLATES,
    fillDemoCopy,
} from "./demoCopy";
import { getDemoServicePeriod } from "./demoDates";
import { fromDemoKey, toDemoKey } from "./demoFilters";
import { createDemoIdFactory, createDemoRandom } from "./demoRandom";
import {
    type DemoClient,
    type DemoPerson,
    FALLBACK_ENTITIES,
    toMessageSentBy,
} from "./demoSources";
import { getDemoTeam, getDemoViewerRecord, pickDemoLead } from "./demoTeam";

const DAY_MS = 24 * 60 * 60_000;
const HOUR_MS = 60 * 60_000;
const UNASSIGNED_EVERY = 7;
const UNASSIGNED_OFFSET = 5;

/** A real close (and stage) a client's tasks link to, so fakes land in real close and stage groups. */
export interface DemoCloseLink {
    workspaceId: string;
    closeId: string;
    closeStageId: string | null;
}

const CLOSE_LINK_SEPARATOR = "|";

/** The real tasks' close links per client, as one string so a refetch with the same links keeps memos. */
export const toDemoCloseLinksKey = (tasks: readonly Task[] | null) =>
    toDemoKey(
        [
            ...new Set(
                (tasks ?? []).flatMap((task) =>
                    task.closeId
                        ? [
                              [
                                  task.workspaceId,
                                  task.closeId,
                                  task.closeStageId ?? "",
                              ].join(CLOSE_LINK_SEPARATOR),
                          ]
                        : [],
                ),
            ),
        ].toSorted(),
    );

export const fromDemoCloseLinksKey = (key: string): DemoCloseLink[] =>
    fromDemoKey(key).map((link) => {
        const [workspaceId, closeId, closeStageId] =
            link.split(CLOSE_LINK_SEPARATOR);
        return {
            workspaceId,
            closeId,
            closeStageId: closeStageId === "" ? null : closeStageId,
        };
    });

export interface DemoTaskSources {
    datasetKey: string;
    organizationId: string | null;
    clients: readonly DemoClient[];
    people: readonly DemoPerson[];
    selfId: number | null;
    closeLinks: readonly DemoCloseLink[];
    count: number;
    /** One run per kind, on tasks spread through the list. */
    runKinds: readonly DemoRunKind[];
    today: string;
    now: number;
}

export interface DemoTaskDataset extends DemoDataset {
    tasks: readonly Task[];
}

const getRunStatus = (kind: DemoRunKind) =>
    kind === "finished" ? TaskStatus.DONE : TaskStatus.IN_PROGRESS;

/** Seeded fake close tasks for the clients with mixed statuses, priorities and due dates, assigned to real teammates, some on runs. */
export const buildDemoTaskDataset = (
    sources: DemoTaskSources,
): DemoTaskDataset => {
    const random = createDemoRandom(sources.datasetKey);
    const ids = createDemoIdFactory(sources.datasetKey);
    const clients = random.shuffle(sources.clients);
    if (clients.length === 0) {
        return { tasks: [], runs: [], viewersByTargetId: new Map() };
    }
    const team = getDemoTeam(sources);
    const { monthName } = getDemoServicePeriod(sources.today);
    const templateOffset = random.int(0, DEMO_TASK_TEMPLATES.length - 1);
    const runSpacing = Math.max(
        1,
        Math.floor(sources.count / sources.runKinds.length),
    );
    const tasks: Task[] = [];
    const runs: DemoRunRecord[] = [];
    const viewersByTargetId = new Map<string, DemoViewerRecord>();

    for (let index = 0; index < sources.count; index++) {
        const client = clients[index % clients.length];
        const entity =
            client.entities.length > 0
                ? client.entities[index % client.entities.length]
                : null;
        const template =
            DEMO_TASK_TEMPLATES[
                (templateOffset + index) % DEMO_TASK_TEMPLATES.length
            ];
        const account = random.pick(DEMO_ACCOUNT_NAMES);
        const title = fillDemoCopy({
            template: template.title,
            month: monthName,
            account,
        });
        const runKindIndex =
            index % runSpacing === 1 % runSpacing
                ? Math.floor(index / runSpacing)
                : -1;
        const runKind =
            runKindIndex >= 0 && runKindIndex < sources.runKinds.length
                ? sources.runKinds[runKindIndex]
                : null;
        const status =
            runKind === null
                ? DEMO_TASK_STATUSES[index % DEMO_TASK_STATUSES.length]
                : getRunStatus(runKind);
        const lead = pickDemoLead({ team, random, index });
        const assignee =
            index % UNASSIGNED_EVERY === UNASSIGNED_OFFSET && runKind === null
                ? null
                : lead;
        const closeLinks = sources.closeLinks.filter(
            (link) => link.workspaceId === client.workspaceId,
        );
        const closeLink =
            closeLinks.at(Math.floor(random.next() * closeLinks.length)) ??
            null;
        const taskId = ids("task", index);
        const dueInDays =
            status === TaskStatus.DONE ? -random.int(1, 4) : random.int(-3, 14);

        tasks.push({
            id: taskId,
            workspaceId: client.workspaceId,
            organizationId: sources.organizationId,
            title,
            description: null,
            assigneeId: assignee?.id ?? null,
            assignee: assignee === null ? null : toMessageSentBy(assignee),
            createdById: lead.id,
            createdAt: new Date(sources.now - random.int(2, 12) * DAY_MS),
            updatedAt: new Date(sources.now - random.int(1, 20) * HOUR_MS),
            status,
            type: template.type,
            priority: DEMO_TASK_PRIORITIES[index % DEMO_TASK_PRIORITIES.length],
            dueDate: addCalendarDays(sources.today, dueInDays),
            closeId: closeLink?.closeId,
            closeStageId: closeLink?.closeStageId,
            entityIds: entity === null ? [] : [entity.id],
        });
        viewersByTargetId.set(
            taskId,
            getDemoViewerRecord({ team, random, index }),
        );
        if (runKind !== null) {
            const reviewer =
                team.others.find((person) => person.id !== lead.id) ?? lead;
            runs.push(
                buildDemoRun({
                    kind: runKind,
                    index: runKindIndex,
                    ids,
                    random,
                    now: sources.now,
                    runTaskId: taskId,
                    taskTitle: title,
                    client,
                    entity:
                        entity ??
                        FALLBACK_ENTITIES[index % FALLBACK_ENTITIES.length],
                    close: null,
                    closeTasks: null,
                    startedBy: lead,
                    reviewer,
                    month: monthName,
                    account,
                }),
            );
        }
    }
    return { tasks, runs, viewersByTargetId };
};
